import { useCallback, useEffect, useRef, useState } from "react";
import SignalingConnection from "../SignalingConnection";

type ManagedSignalingOptions = {
  socketURL: string;
  onMessage: (message: any) => void;
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
  rotationMs?: number;
};

const defaultReconnectBaseMs = 1000;
const defaultReconnectMaxMs = 30000;
const defaultRotationMs = 90 * 60 * 1000;

export default function useManagedSignalingConnection({
  socketURL,
  onMessage,
  reconnectBaseMs = defaultReconnectBaseMs,
  reconnectMaxMs = defaultReconnectMaxMs,
  rotationMs = defaultRotationMs,
}: ManagedSignalingOptions) {
  const onMessageRef = useRef(onMessage);
  const connectionRef = useRef<SignalingConnection | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rotationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const mountedRef = useRef(false);
  const openRef = useRef(false);
  const deliberateClosesRef = useRef(new WeakSet<SignalingConnection>());
  const connectRef = useRef<() => void>(() => undefined);
  const restartRef = useRef<(connection: SignalingConnection) => void>(
    () => undefined,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [connectionCount, setConnectionCount] = useState(0);

  onMessageRef.current = onMessage;

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearRotationTimer = useCallback(() => {
    if (rotationTimerRef.current !== null) {
      clearTimeout(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current || reconnectTimerRef.current !== null) {
      return;
    }

    const delay = Math.min(
      reconnectBaseMs * 2 ** reconnectAttemptRef.current,
      reconnectMaxMs,
    );
    reconnectAttemptRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectRef.current();
    }, delay);
  }, [reconnectBaseMs, reconnectMaxMs]);

  const restartConnection = useCallback(
    (connection: SignalingConnection) => {
      if (!mountedRef.current || connectionRef.current !== connection) {
        return;
      }

      deliberateClosesRef.current.add(connection);
      connectionRef.current = null;
      openRef.current = false;
      setIsOpen(false);
      clearRotationTimer();
      connection.close();
      scheduleReconnect();
    },
    [clearRotationTimer, scheduleReconnect],
  );
  restartRef.current = restartConnection;

  const connect = useCallback(() => {
    if (!mountedRef.current || connectionRef.current !== null) {
      return;
    }

    let connection: SignalingConnection;
    connection = new SignalingConnection({
      socketURL,
      onMessage: (message) => {
        if (mountedRef.current && connectionRef.current === connection) {
          onMessageRef.current(message);
        }
      },
      onOpen: () => {
        if (!mountedRef.current || connectionRef.current !== connection) {
          deliberateClosesRef.current.add(connection);
          connection.close();
          return;
        }

        reconnectAttemptRef.current = 0;
        clearReconnectTimer();
        clearRotationTimer();
        openRef.current = true;
        setIsOpen(true);
        setConnectionCount((count) => count + 1);
        rotationTimerRef.current = setTimeout(() => {
          rotationTimerRef.current = null;
          restartRef.current(connection);
        }, rotationMs);
      },
      onClose: () => {
        const wasDeliberate = deliberateClosesRef.current.has(connection);
        deliberateClosesRef.current.delete(connection);

        if (connectionRef.current !== connection) {
          return;
        }

        connectionRef.current = null;
        openRef.current = false;
        clearRotationTimer();
        if (!mountedRef.current) {
          return;
        }

        setIsOpen(false);
        if (!wasDeliberate) {
          scheduleReconnect();
        }
      },
      onHeartbeatTimeout: () => restartRef.current(connection),
    });
    connectionRef.current = connection;
  }, [
    clearReconnectTimer,
    clearRotationTimer,
    rotationMs,
    scheduleReconnect,
    socketURL,
  ]);
  connectRef.current = connect;

  useEffect(() => {
    const deliberateCloses = deliberateClosesRef.current;
    mountedRef.current = true;
    connectRef.current();

    return () => {
      mountedRef.current = false;
      openRef.current = false;
      clearReconnectTimer();
      clearRotationTimer();

      const connection = connectionRef.current;
      if (connection) {
        deliberateCloses.add(connection);
        connectionRef.current = null;
        connection.close();
      }
    };
  }, [clearReconnectTimer, clearRotationTimer]);

  const sendToServer = useCallback((message: unknown): boolean => {
    const connection = connectionRef.current;
    if (!connection || !openRef.current) {
      return false;
    }

    connection.sendToServer(message);
    return true;
  }, []);

  return { isOpen, connectionCount, sendToServer };
}
